/**
 * ToolResultCard component
 * Generic wrapper for displaying tool execution results in the chatbot
 */
import { useState, type ReactNode } from 'react'
import { ChevronDown, ChevronUp, AlertCircle, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface ToolResultCardProps {
  /** Name of the tool that was executed */
  toolName: string
  /** Whether the tool is currently loading */
  isLoading?: boolean
  /** Error message if tool execution failed */
  error?: string
  /** Timestamp of tool execution */
  timestamp?: string
  /** Whether the card content is collapsible */
  isCollapsible?: boolean
  /** Content to display inside the card */
  children?: ReactNode
}

export function ToolResultCard({
  toolName,
  isLoading = false,
  error,
  timestamp,
  isCollapsible = false,
  children,
}: ToolResultCardProps) {
  const [isExpanded, setIsExpanded] = useState(true)

  const formatTimestamp = (ts: string) => {
    const date = new Date(ts)
    return date.toLocaleTimeString()
  }

  return (
    <div
      data-testid="tool-result-card"
      data-status={error ? 'error' : undefined}
      role="region"
      aria-label={`Tool result: ${toolName}`}
      className={cn(
        'rounded-xl border bg-card text-card-foreground shadow-sm overflow-hidden',
        'border-l-[3px]',
        error ? 'border-l-destructive' : 'border-l-primary',
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b bg-muted/30">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs font-medium">{toolName}</span>
          {timestamp && (
            <span
              data-testid="tool-timestamp"
              className="text-xs text-muted-foreground"
            >
              {formatTimestamp(timestamp)}
            </span>
          )}
        </div>
        {isCollapsible && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
            className="p-1 hover:bg-muted rounded"
          >
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
        )}
      </div>

      {/* Content */}
      <div
        className={cn(
          'px-4 py-3',
          isCollapsible && !isExpanded && 'hidden'
        )}
        style={{ display: isCollapsible && !isExpanded ? 'none' : undefined }}
      >
        {isLoading ? (
          <div
            data-testid="tool-loading-indicator"
            className="flex items-center gap-2 text-muted-foreground"
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Loading...</span>
          </div>
        ) : (
          <>
            {error && (
              <div className="flex items-start gap-2 text-destructive mb-3">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}
            {children}
          </>
        )}
      </div>
    </div>
  )
}
